import { setConfirmEndExam, setExamStatsTab, setSelectedApplicantsIds } from '../../../../../store/workspace/slice';
import { getExaminersThunk, updateExaminersStatusThunk } from '../../../../../store/workspace/thunks';
import { AshbyLogoIcon, DownloadIcon, NoteIcon, PrintIcon, RefreshIcon } from '../../../../../assets/icons';
import { examStatsDefaultActions, interviewActions } from '../../../../../configs';
import RecruitingInterviewTable from '../../components/RecruitingInterviewTable';
import MultipleChoiceExamTable from '../../components/MultipleChoiceExamTable';
import { camelCaseToSpacedString } from '../../../../../utils/generalHelpers';
import { useDispatch, useSelector } from '../../../../../store/store';
import { ExamStatsTab } from '../../../../../store/workspace/types';
import { selectUserId } from '../../../../../store/auth/selectors';
import FinalResultTable from '../../components/FinalResultTable';
import CodingExamTable from '../../components/CodingExamTable';
import { ListItem } from '../../../../../store/general/types';
import InterviewTable from '../../components/InterviewTable';
import DropDown from '../../../../../components/DropDown';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../../../../../components/Button';
import { useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import styles from './style.module.scss';
import {
    selectExamStatsTab,
    selectCodingExamStats,
    selectExamStatsJobInfo,
    selectFinalResultStats,
    selectSelectedApplicantsIds,
    selectPersonalInterviewStats,
    selectMultipleChoiceExamStats,
    selectRecruitingInterviewStatists,
} from '../../../../../store/workspace/selectors';
import {
    exportCodingExamTableToExcel,
    exportFinalResultTableToExcel,
    exportPersonalInterviewTableToExcel,
    exportMultipleChoiceExamTableToExcel,
    exportRecruitingInterviewTableToExcel,
} from '../../../../../utils/exportHelpers';

function ExaminersStatics() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { jobId, examId } = useParams();
    const userId = useSelector(selectUserId);
    const [searchTerm, setSearchTerm] = useState('');
    const [examinersType, setExaminersType] = useState<'Qualified' | 'All'>('All');
    const [action, setAction] = useState<ListItem | null>(null);

    const [updatingStatus, setUpdatingStatus] = useState(false);
    const selectedApplicantsIds = useSelector(selectSelectedApplicantsIds);

    const jobInfo = useSelector(selectExamStatsJobInfo);
    const tab = useSelector(selectExamStatsTab);
    const recruitingInterview = useSelector(selectRecruitingInterviewStatists);
    const multipleChoiceExam = useSelector(selectMultipleChoiceExamStats);
    const codingExam = useSelector(selectCodingExamStats);
    const interview = useSelector(selectPersonalInterviewStats);
    const finalResult = useSelector(selectFinalResultStats);
    const tableRef = useRef<HTMLDivElement>(null);


    useEffect(() => {
        dispatch(setSelectedApplicantsIds([]));

        if (!tab ||
            (tab === 'coding' && !codingExam) ||
            (tab === 'recruitingInterview' && !recruitingInterview) ||
            (tab === 'multipleChoice' && !multipleChoiceExam) ||
            (tab === 'interview' && !interview) ||
            (tab === 'finalResult' && !finalResult)
        ) {
            fetchTablesData(tab);
        }
    }, [tab]);

    const fetchTablesData = (tab: ExamStatsTab) => {
        if (!jobId) return;
        dispatch(getExaminersThunk({ tab, jobId }))
    };

    // const changeExaminersType = (type: 'Qualified' | 'All') => {
    //     setExaminersType(type);
    // };

    const downloadTable = () => {
        switch (tab) {
            case 'coding':
                exportCodingExamTableToExcel(codingExam?.examiners || []);
                break;
            case 'multipleChoice':
                exportMultipleChoiceExamTableToExcel(multipleChoiceExam?.examiners || []);
                break;
            case 'recruitingInterview':
                exportRecruitingInterviewTableToExcel(recruitingInterview?.examiners || []);
                break;
            case 'interview':
                exportPersonalInterviewTableToExcel(interview?.examiners || []);
                break;
            case 'finalResult':
                exportFinalResultTableToExcel(finalResult?.examiners || []);
                break;
            default:
                break;
        }
    };

    const updateExaminersStatus = async () => {
        try {
            setUpdatingStatus(true);
            const body = {
                jobId,
                userId,
                status: action?.id,
                examinerIds: selectedApplicantsIds,
            };

            await dispatch(updateExaminersStatusThunk(body));
            dispatch(getExaminersThunk({ userId, jobId, examId }));
            setAction(null);
            dispatch(setSelectedApplicantsIds([]));
            setUpdatingStatus(false);
        } catch (e: unknown) {
            console.warn(e);
        }
    };

    const endExam = () => {
        dispatch(setConfirmEndExam(true));
    };

    const changeTab = (status: 'pending' | 'enable' | 'disable', tab: ExamStatsTab) => {
        if (status !== 'enable') return;
        dispatch(setExamStatsTab(tab));
        setExaminersType('All');
    };

    const renderActionList = () => {
        switch (tab) {
            case 'recruitingInterview': {
                const check = jobInfo?.recruitingInterview === 'enable' &&
                    jobInfo.multipleChoiceExam === 'disable' &&
                    jobInfo.codingExam === 'disable';
                return check ? examStatsDefaultActions : undefined;
            }
            case 'multipleChoice': {
                const check = (jobInfo?.recruitingInterview === 'disable' &&
                    jobInfo.multipleChoiceExam === 'enable' &&
                    jobInfo.codingExam === 'disable') || (jobInfo?.recruitingInterview === 'enable' &&
                        jobInfo.multipleChoiceExam === 'enable' &&
                        jobInfo.codingExam === 'disable');
                return check ? examStatsDefaultActions : undefined;
            }
            case 'coding': {
                const check = jobInfo?.codingExam === 'enable';
                return check ? examStatsDefaultActions : undefined;
            }
            case 'interview':
                return interviewActions;
            default:
                return undefined;
        }
    };

    const handlePrint = useReactToPrint({
        contentRef: tableRef,
        documentTitle: 'Applicants list'
    });


    return (
        <div className={styles.container}>
            <div className={styles.main}>
                <div className={styles.main_header}>
                    <span className={styles.main_header_title}>
                        <span>{jobInfo?.title}</span>
                        {jobInfo?.platform === 'Ashby' && <AshbyLogoIcon width={71} height={23} />}
                    </span>
                    {(tab !== 'interview' && tab !== 'finalResult') &&
                        <Button
                            type='Primary'
                            text='Exam sheet'
                            icon={<NoteIcon />}
                            iconPosition='Left'
                            onClick={() => navigate('sheet')}
                        />}
                </div>

                <div className={styles.main_tab}>
                    {jobInfo?.recruitingInterview === 'enable' &&
                        <div
                            className={styles.main_tab_item}
                            data-is-active={tab === 'recruitingInterview'}
                            onClick={() => changeTab(jobInfo?.recruitingInterview, 'recruitingInterview')}
                        >
                            <span>Recruiting interview</span>
                        </div>
                    }
                    {(jobInfo?.multipleChoiceExam === 'enable' ||
                        jobInfo?.multipleChoiceExam === 'pending') &&
                        <div
                            className={styles.main_tab_item}
                            data-is-active={tab === 'multipleChoice'}
                            onClick={() => changeTab(jobInfo?.multipleChoiceExam, 'multipleChoice')}
                            data-is-disable={jobInfo?.multipleChoiceExam === 'pending'}
                        >
                            <span>Multiple choice exam</span>
                        </div>
                    }
                    {(jobInfo?.codingExam === 'enable' ||
                        jobInfo?.codingExam === 'pending') && <div
                            className={styles.main_tab_item}
                            data-is-active={tab === 'coding'}
                            onClick={() => changeTab(jobInfo?.codingExam, 'coding')}
                            data-is-disable={jobInfo?.codingExam === 'pending'}
                        >
                            <span>Coding exam</span>
                        </div>}
                    <div
                        className={styles.main_tab_item}
                        data-is-active={tab === 'interview'}
                        onClick={() => changeTab('enable', 'interview')}
                    >
                        <span>Personal interview</span>
                    </div>
                    <div
                        className={styles.main_tab_item}
                        data-is-active={tab === 'finalResult'}
                        onClick={() => changeTab('enable', 'finalResult')}
                    >
                        <span>Final results</span>
                    </div>
                </div>

                <DropDown
                    idKey='id'
                    titleKey='title'
                    containerClassName={styles.main_tab_dropdown}
                    selectedItem={{ id: tab, title: camelCaseToSpacedString(tab || '') }}
                    setItem={(item: any) => changeTab('enable', item.id as ExamStatsTab)}
                    dropDownList={[
                        { id: 'recruitingInterview', title: 'Recruiting interview' },
                        { id: 'multipleChoice', title: 'Multiple choice exam' },
                        { id: 'coding', title: 'Coding exam' },
                        { id: 'interview', title: 'Personal interview' },
                        { id: 'finalResult', title: 'Final results' },
                    ]}
                />

                <div className={styles.main_table}>
                    <div className={styles.main_table_header}>
                        <span>Applicants list</span>
                        <div className={styles.main_table_header_actions}>
                            <Button
                                type='Light'
                                text='Download'
                                iconPosition='Right'
                                icon={<DownloadIcon />}
                                onClick={downloadTable}
                                className={styles.main_table_header_actions_btn}
                            />
                            <Button
                                type='Light'
                                text='Print list'
                                iconPosition='Right'
                                icon={<PrintIcon />}
                                onClick={handlePrint}
                                className={styles.main_table_header_actions_btn}
                            />
                            <Button
                                type='Light'
                                text='Refresh list'
                                iconPosition='Right'
                                icon={<RefreshIcon />}
                                onClick={() => fetchTablesData(tab)}
                                className={styles.main_table_header_actions_btn}
                            />
                        </div>
                    </div>

                    {tab === 'recruitingInterview' && <RecruitingInterviewTable
                        ref={tableRef}
                        jobId={jobId}
                        statics={recruitingInterview}
                        selectable={Boolean(renderActionList())}
                    />}

                    {tab === 'multipleChoice' && <MultipleChoiceExamTable
                        jobId={jobId}
                        ref={tableRef}
                        examinersType={examinersType}
                        selectable={Boolean(renderActionList())}
                        examinersInfo={multipleChoiceExam?.examiners || []}
                    />}

                    {tab === 'coding' && <CodingExamTable
                        jobId={jobId}
                        ref={tableRef}
                        selectable={Boolean(renderActionList())}
                        examinersInfo={codingExam?.examiners || []}
                    />}

                    {tab === 'interview' && <InterviewTable
                        jobId={jobId}
                        ref={tableRef}
                        examinersData={interview?.examiners || []}
                    />}

                    {tab === 'finalResult' && <FinalResultTable
                        jobId={jobId}
                        ref={tableRef}
                        fetchTablesData={fetchTablesData}
                        examinersData={finalResult?.examiners || []}
                    />}
                </div>

                {renderActionList() && <div className={styles.main_action}>
                    <span className={styles.main_action_count}>
                        {selectedApplicantsIds.length} {selectedApplicantsIds.length > 1 ? 'Accounts' : 'Account'} Selected
                    </span>
                    <DropDown
                        idKey='id'
                        openListOnTop
                        titleKey='title'
                        setItem={setAction}
                        selectedItem={action}
                        placeholder='Select an action'
                        dropDownList={renderActionList()}
                        containerClassName={styles.main_action_dropdown}
                        wrapperClassName={styles.main_action_dropdown_wrapper}
                    />
                    <Button
                        type='Primary'
                        text='Submit'
                        loading={updatingStatus}
                        onClick={updateExaminersStatus}
                        disabled={selectedApplicantsIds.length === 0 || !action}
                    />
                </div>}
            </div>
        </div>
    )
};

export default ExaminersStatics;
